<template>
  <div>
    <v-card
      elevation="5"
    >
      <v-toolbar
        color="primaryLight"
        dark
        dense
      >
        <v-toolbar-title>Planes de operaciones</v-toolbar-title>

        <v-spacer />

        <!-- Botón de descarga general -->
        <v-menu
          v-if="planesOperaciones?.length > 0"
          bottom
          offset-y
        >
          <template
            #activator="{ on, attrs }"
          >
            <v-tooltip
              bottom
            >
              <template
                #activator="{ on: onTooltip, attrs: attrsTooltip }"
              >
                <v-btn
                  fab
                  small
                  v-bind="{ ...attrs, ...attrsTooltip }"
                  color="secondary"
                  right
                  absolute
                  :style="{marginRight: '3.2rem'}"
                  v-on="{ ...on, ...onTooltip }"
                >
                  <v-icon
                    color="primary"
                  >
                    mdi-download
                  </v-icon>
                </v-btn>
              </template>
              <span>Descargar planes visibles</span>
            </v-tooltip>
          </template>
          <v-list>
            <v-list-item
              @click="exportarPlanesVisiblesSHP"
            >
              <v-list-item-title>Descargar SHP</v-list-item-title>
            </v-list-item>
            <v-list-item
              @click="exportarPlanesVisiblesPDF"
            >
              <v-list-item-title>Descargar PDF</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <!-- Botón de añadir plan -->
        <v-tooltip
          bottom
        >
          <template
            #activator="{ on, attrs }"
          >
            <v-btn
              fab
              small
              v-bind="attrs"
              color="secondary"
              right
              absolute
              :disabled="disabledByRol || (isSymbologyPanelLocked && !isSymbologyPPOOOpen)"
              v-on="on"
              @click="openAddPlanDialog"
            >
              <v-icon
                color="primary"
              >
                mdi-plus
              </v-icon>
            </v-btn>
          </template>
          <span>Añadir plan de operaciones</span>
        </v-tooltip>
      </v-toolbar>

      <v-skeleton-loader
        v-if="loading"
        type="table-heading@3"
      />
      <v-list
        v-else
        subheader
      >
        <template
          v-for="(plan, index) in planesOperaciones"
        >
          <div>
            <v-list-item
              :class="{ 'plan-hovered': hoveredPlanId === plan.ID_PLAN_OPERACIONES }"
              @mouseenter="hoveredPlanId = plan.ID_PLAN_OPERACIONES"
              @mouseleave="hoveredPlanId = null"
            >
              <v-list-item-content>
                <v-list-item-title>{{ plan.NOMBRE }}</v-list-item-title>
                <v-list-item-subtitle>{{ formatDate(plan.FECHA) }}</v-list-item-subtitle>
              </v-list-item-content>

              <v-list-item-action
                v-if="hoveredPlanId === plan.ID_PLAN_OPERACIONES"
              >
                <v-tooltip
                  bottom
                >
                  <template
                    #activator="{ on, attrs }"
                  >
                    <v-btn
                      icon
                      v-bind="attrs"
                      :disabled="showDialog || disabledByRol || (isSymbologyPanelLocked && !isSymbologyPPOOOpen)"
                      v-on="on"
                      @click="openEditPlanDialog(plan)"
                    >
                      <v-icon>mdi-pencil</v-icon>
                    </v-btn>
                  </template>
                  <span>Editar</span>
                </v-tooltip>
              </v-list-item-action>

              <v-list-item-action
                v-if="hoveredPlanId === plan.ID_PLAN_OPERACIONES || planesActivos.includes(plan.ID_PLAN_OPERACIONES)"
              >
                <v-tooltip
                  bottom
                >
                  <template
                    #activator="{ on, attrs }"
                  >
                    <v-btn
                      icon
                      v-bind="attrs"
                      :disabled="showDialog"
                      v-on="on"
                      @click="mostrarPlan(plan)"
                    >
                      <v-icon>{{ planesActivos.includes(plan.ID_PLAN_OPERACIONES) ? 'mdi-eye-off' : 'mdi-eye' }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ plan.ID_PLAN_OPERACIONES === idPlanShowing ? 'Ocultar' : 'Visualizar' }}</span>
                </v-tooltip>
              </v-list-item-action>
            </v-list-item>

            <v-divider
              v-if="index < planesOperaciones?.length - 1"
              style="margin-right: 1rem; margin-left: 1rem;"
            />
          </div>
        </template>

        <p
          v-if="planesOperaciones?.length <= 0"
          align="center"
          style="margin: 16px 10px 10px 10px; color: gray;"
        >
          No hay ningún plan de operaciones
        </p>
      </v-list>
    </v-card>

    <vx-dialog-plan-operaciones
      :show="showDialog"
      :id-incendio="idIncendio"
      :plan-selected="planSelected"
      :is-edit="isEdit"
      @open="showDialog = true"
      @close="showDialog = false"
      @hideLeftMenu="hideLeftMenu"
    />
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import * as ArcGIS from '@/helpers/ArcGIS'
import DialogPlanOperaciones from './DialogPlanOperaciones'

import constants from '@/helpers/constants'

import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { normalizeFileName } from '@/helpers/Utils'
pdfMake.vfs = pdfFonts.vfs

const shpwrite = require('shp-write-update')

export default {

  components: {
    'vx-dialog-plan-operaciones': DialogPlanOperaciones
  },
  props: {
    idIncendio: String,
    loading: Boolean
  },

  data: () => ({
    showDialog: false,
    isEdit: false,
    idPlanShowing: null,
    planSelected: {},
    planesActivos: [],
    hoveredPlanId: null
  }),

  computed: {
    ...mapGetters({
      getIncendioByID: 'incendio/getIncendioByID',
      isCurrentUsuarioAdmin: 'usuario/isCurrentUsuarioAdmin',
      isSymbologyPanelLocked: 'map/isSymbologyPanelLocked',
      isSymbologyPPOOOpen: 'map/isSymbologyPPOOOpen'
    }),

    incendio() {
      return this.getIncendioByID(this.idIncendio)
    },

    planesOperaciones() {
      let planes = []

      if (this.incendio) {
        planes = this.incendio.PLANES_OPERACIONES
      }
      return planes
    },
    disabledByRol() {
      // Deshabilita funciones dependiendo del rol
      return !this.isCurrentUsuarioAdmin
    }
  },

  watch: {
    showPlanesOperaciones() {
      if (this.showPlanesOperaciones) {
        this.idPlanShowing = null
      }
    },

    planesOperaciones() {
      if (this.idPlanShowing && this.planesOperaciones) {
        const plan = this.planesOperaciones.find(x => x.ID_PLAN_OPERACIONES === this.idPlanShowing)
        if (plan) {
          ArcGIS.drawGraphicsPlanOperaciones(this.planesOperaciones.find(x => x.ID_PLAN_OPERACIONES === this.idPlanShowing).GRAFICOS) // Repintar graficos del plan abierto
        }
      }
    }
  },

  mounted() {
    this.$eventHub.$on('mapImage', this.exportarPlanPDFCallBack)
  },

  methods: {
    ...mapActions('shareMap', ['paintPlanOperaciones']),

    formatDate(date) {
      return this.$date.formatDate(date, 'LLL')
    },

    openAddPlanDialog() {
      this.mostrarPlan(null)
      this.isEdit = false
      this.showDialog = true
    },

    openEditPlanDialog(plan) {
      this.mostrarPlan(null)
      this.planSelected = plan
      this.isEdit = true
      this.showDialog = true
    },

    async mostrarPlan(plan) {
      if (!plan) {
        this.planSelected = null
        this.planesActivos = []
        this.idPlanShowing = null

        this.paintPlanOperaciones([])
        await ArcGIS.drawGraphicsPlanOperaciones([])
        return
      }

      this.planSelected = plan
      const planId = plan.ID_PLAN_OPERACIONES

      const planAlreadyShown = this.planesActivos.includes(planId)

      if (planAlreadyShown) {
        this.planesActivos = this.planesActivos.filter(id => id !== planId)
      } else {
        this.planesActivos.push(planId)
      }

      const planesShown = this.planesOperaciones.filter(p => this.planesActivos.includes(p.ID_PLAN_OPERACIONES))
      const graficos = planesShown.flatMap(p => p.GRAFICOS)

      this.paintPlanOperaciones(graficos)
      await ArcGIS.drawGraphicsPlanOperaciones(graficos, this)
    },

    async exportarPlanesVisiblesSHP() {
      const planesVisibles = this.planesOperaciones.filter(plan =>
        this.planesActivos.includes(plan.ID_PLAN_OPERACIONES)
      )

      if (!planesVisibles.length) {
        this.$notify('No hay planes de operaciones visibles en el mapa para exportar.')
        return
      }

      const defaultFeature = {
        type: 'Feature',
        geometry: {
          type: '',
          coordinates: []
        },
        properties: {}
      }

      const features = []

      planesVisibles.forEach(plan => {
        plan.GRAFICOS.forEach(grafico => {
          const feature = JSON.parse(JSON.stringify(defaultFeature))
          const parsedGeometry = JSON.parse(grafico.GEOMETRIA)

          switch (grafico.TIPO) {
            case 'point':
            case 'icon':
            case 'text': {
              feature.geometry.type = 'Point'
              feature.geometry.coordinates = ArcGIS.convertirMercXYToLatLon(
                parsedGeometry[0],
                parsedGeometry[1]
              )
              break
            }
            case 'polyline': {
              feature.geometry.type = 'LineString'
              feature.geometry.coordinates = parsedGeometry.map(([x, y]) =>
                ArcGIS.convertirMercXYToLatLon(x, y)
              )
              break
            }
            case 'polygon': {
              feature.geometry.type = 'Polygon'
              const ring = parsedGeometry.map(([x, y]) =>
                ArcGIS.convertirMercXYToLatLon(x, y)
              )
              feature.geometry.coordinates = [ring]
              break
            }
            default:
              return
          }

          feature.properties = {
            ...grafico.ATRIBUTOS,
            PLAN_NOMBRE: plan.NOMBRE,
            PLAN_FECHA: plan.FECHA
          }

          features.push(feature)
        })
      })

      const geoJson = {
        type: 'FeatureCollection',
        features
      }

      const now = this.$date.now().format('YYYY_MM_DD_HH_mm')
      const municipio = this.incendio.MUNICIPIO
      const baseName = normalizeFileName(`SITAC_${municipio}`)
      const zipName = `${baseName}_${now}.zip`

      const options = {
        folder: `${baseName}_${now}`,
        types: { point: 'PUNTOS', line: 'LINEAS', polygon: 'POLIGONOS' },
        outputType: 'blob',
        compression: 'DEFLATE'
      }

      const zipBlob = await shpwrite.zip(geoJson, options)

      const url = URL.createObjectURL(zipBlob)
      const a = document.createElement('a')
      a.href = url
      a.download = zipName
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    },

    async exportarPlanesVisiblesPDF() {
      const planesVisibles = this.planesOperaciones.filter(plan =>
        this.planesActivos.includes(plan.ID_PLAN_OPERACIONES)
      )
      if (!planesVisibles.length) {
        this.$notify('No hay planes de operaciones visibles en el mapa para exportar.')
        return
      }
      try {
        const image = await ArcGIS.captureMap()
        this.exportarPlanPDFCallBack(image)
      } catch (error) {
        console.error('Error exportando pdf: ', error)
      }
    },

    exportarPlanPDFCallBack(captura) {
      const planesVisibles = this.planesOperaciones.filter(plan =>
        this.planesActivos.includes(plan.ID_PLAN_OPERACIONES)
      )
      const docDefinition = {
        pageOrientation: 'portrait',
        content: [

        ],
        styles: {
          header: {
            fontSize: 18,
            margin: [0, 0, 0, 10],
            bold: true
          },
          subheader: {
            fontSize: 12
            // bold: true
          },
          quote: {
            italics: true
          },
          small: {
            fontSize: 8
          }
        }
      }
      docDefinition.content.push(constants.headerPDF)
      docDefinition.content.push(
        {
          text: `Planes de operaciones - ${this.incendio.MUNICIPIO}`,
          style: 'header'
        }
      )
      planesVisibles.forEach(plan => {
        docDefinition.content.push({ text: `${plan.NOMBRE} - ${this.$date.parseDate(plan.FECHA).format('YYYY/MM/DD HH:mm')}`, style: 'subheader' })
        docDefinition.content.push({ text: `${plan.DESCRIPCION}\n\n` || 'Sin descripción.' + '\n\n', style: 'text' })
      })

      docDefinition.content.push({ image: captura, width: 500, style: 'sectionImage' })

      const fileName = normalizeFileName(`SITAC_${this.incendio.MUNICIPIO}_${this.$date.now().format('YYYY_MM_DD_hh_mm')}`)
      pdfMake.createPdf(docDefinition).download(fileName)
    },

    hideLeftMenu() {
      this.$emit('hideLeftMenu')
    }
  }
}
</script>

<style scoped>
.plan-hovered {
  background-color: rgb(0,0,0,0.05)
}
</style>
